import { computed, getCurrentInstance, nextTick, onMounted, ref, toRef, watch } from 'vue'
import type { Pinia } from 'pinia'
import { getDangerSignQuestionOrder } from '@shared/assessment-definition/dist/configurations/danger-signs'
import { toConversionObservations, useGuidedModeStore } from '../stores/guidedMode'
import { usePartnerConfigStore } from '@/stores/partnerConfig'
import { usePreferenceStore } from '@/stores/preference'
import { usePatientStore } from '@/features/text-mode/stores/patient.store'
import { buildGuidedModeObservations } from '../utils/mrx-observations'
import {
  assessmentQuestionSteps,
  DEMOGRAPHIC_CONCEPT_IDS,
  questionForStep,
} from '../utils/assessment-definition'
import { withDangerSignScreen } from '../utils/danger-sign-screen'
import {
  findFirstOpenStepIndex,
  findNextUnansweredStepIndex,
  hasOpenVisibleQuestion,
  isStepAnswered,
  isStepVisible,
  stepKey,
} from '../utils/step-engine'
import { useGuidedDemographics } from './useGuidedDemographics'
import { useGuidedReviewFlow } from './useGuidedReviewFlow'
import { useGuidedQuestionnaire } from './useGuidedQuestionnaire'
import { useGuidedSymptoms } from './useGuidedSymptoms'
import { useGuidedFocusedPlan } from './useGuidedFocusedPlan'
import { useGuidedMediaPresentation } from './useGuidedMediaPresentation'
import type { GuidedMediaItem } from './useGuidedMediaPresentation'
import {
  buildGuidedPatient,
  buildReviewObservations,
  flattenSteps,
} from '../utils/guided-observations'
import { applyGuidedAssessment } from '../services/guided-mode-submit.service'
import { prepareGuidedWeightOnStop } from '../services/guided-stop-weight.service'
import { evaluateGuidedEscalation } from '../services/guided-escalation.service'
import { setTrackByPatientType } from '@/features/text-mode/services/text-mode-submit.service'
import { setActiveTextModeContext } from '@/features/text-mode/analytics/text-mode-analytics'
import { getActiveTextModeContext } from '@/features/text-mode/analytics/text-mode-context'
import { useEncounterStore } from '@/stores/encounter'
import { useTrackStore } from '@/stores/track'
import { useOverallAssessmentStore } from '@/features/overall-assessment/stores/overallAssessment'
import { useDangerSignCheck } from '@/features/danger-signs/composables/useDangerSignCheck'
import { onDangerSignStop } from '@/features/danger-signs/composables/onDangerSignStop'
import type {
  GuidedAnswer,
  GuidedEscalationResult,
  GuidedModeClassification,
  GuidedModeObservation,
  GuidedModeStep,
} from '../types/guided-mode.types'
import {
  GUIDED_DEMOGRAPHICS_PANEL,
  GUIDED_SUMMARY_PANEL,
  GUIDED_SYMPTOMS_PANEL,
  trackGuidedAnswerEdited,
  trackGuidedAssessmentCompleted,
  trackGuidedAssessmentSelected,
  trackGuidedModeStarted,
  trackGuidedQuestionAnswered,
  trackGuidedQuestionViewed,
  trackGuidedSummaryViewed,
} from '../analytics/guided-mode-analytics'
import { safePush } from '@/modules/navigation'
import { logger } from '@/logging/logger'
import { isNetworkError } from '@/utils/networkError'
import { LogCategory } from '@/logging/types'

const SHARED_JOURNEY_ROUTE = '/overall-assessment'

interface GuidedModeVueProxy {
  $pinia?: Pinia
  $t?: (token: string, values?: Record<string, string | number>) => string
  $d?: (date: Date, format: string) => string
  $n?: (value: number, format: string) => string
  $route?: { name?: string | null; query?: Record<string, unknown> }
  $router?: { back: () => void; push: (path: string) => Promise<unknown> | void }
  $sentry?: { captureException: (error: unknown) => void }
}

type PreferredWidgetGetter = 'getHeartRateInputMethod' | 'getRespiratoryRateInputMethod'

const PREFERRED_WIDGET_GETTERS: Record<string, PreferredWidgetGetter> = {
  'concept.heart_rate': 'getHeartRateInputMethod',
  'concept.respiratory_rate': 'getRespiratoryRateInputMethod',
}

function hasAnswer(answer: GuidedAnswer | undefined): boolean {
  return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
}

export function useGuidedModeWorkflow() {
  const instance = getCurrentInstance()
  const proxy = instance?.proxy as unknown as GuidedModeVueProxy | undefined
  const guidedModeStore = useGuidedModeStore(proxy?.$pinia)
  const partnerConfigStore = usePartnerConfigStore(proxy?.$pinia)
  const preferenceStore = usePreferenceStore(proxy?.$pinia)
  const encounterStore = useEncounterStore(proxy?.$pinia)
  const trackStore = useTrackStore(proxy?.$pinia)
  const patientStore = usePatientStore(proxy?.$pinia)
  const overallAssessmentStore = useOverallAssessmentStore(proxy?.$pinia)
  const dangerSignCheck = useDangerSignCheck()
  let isFinishing = false
  let navigationToken = 0
  const isEvaluatingEscalation = ref(false)
  const networkError = ref(false)
  const navigating = ref(false)
  const navigationSteps = ref<GuidedModeStep[] | null>(null)
  let completionReturnKey = ''
  let revealAnsweredStepKey = ''
  let evaluatedScope: { result: GuidedEscalationResult; inputs: string } | null = null
  const settledConceptIds = ref<string[]>([])
  const view = toRef(guidedModeStore, 'view')
  watch(view, value => {
    if (value === 'summary') guidedModeStore.summaryReached = true
  }, { immediate: true })
  // The index is a compatibility projection; the presented page keeps its identity during re-planning.
  const currentIndex = computed({
    get: () => {
      const id = guidedModeStore.focusedCurrentPageId
      if (view.value === 'question' && guidedModeStore.consultationPath === 'focused' && id) {
        const index = visibleSteps.value.findIndex(step => stepKey(step) === id)
        if (index >= 0) return index
      }
      return guidedModeStore.currentIndex
    },
    set: (index: number) => {
      guidedModeStore.currentIndex = index
      guidedModeStore.focusedCurrentPageId = view.value === 'question'
        && guidedModeStore.consultationPath === 'focused' && visibleSteps.value[index]
        ? stepKey(visibleSteps.value[index]) : null
    },
  })
  const answers = computed({
    get: () => guidedModeStore.effectiveAnswers,
    set: values => guidedModeStore.setAnswers(values),
  })
  const activeAssessmentPath = toRef(guidedModeStore, 'activeAssessmentPath')
  const requestedAssessmentPath = toRef(guidedModeStore, 'requestedAssessmentPath')
  const age = toRef(guidedModeStore, 'age')
  const ageUnit = toRef(guidedModeStore, 'ageUnit')
  const sex = toRef(guidedModeStore, 'sex')
  const maternalStatus = toRef(guidedModeStore, 'maternalStatus')
  const identifierValue = toRef(guidedModeStore, 'identifierValue')

  const assessmentDefinitions = computed(() => guidedModeStore.clinicalContent.assessmentDefinitions || {})
  const hasDefinitions = computed(() => Object.keys(assessmentDefinitions.value).length > 0)
  const demographics = useGuidedDemographics({
    activeAssessmentPath,
    requestedAssessmentPath,
    assessmentDefinitions,
    identifierConfig: computed(() => partnerConfigStore.demographicsIdentifier),
    tracks: computed(() => guidedModeStore.tracks),
    translate: translationFor,
    age,
    ageUnit,
    sex,
    maternalStatus,
    identifierValue,
  })
  const {
    editingDemographics, ageMinimum, ageMaximum, ageInvalid, ageError,
    demographicAgePrompt, demographicAgeTitle, demographicSexPrompt, demographicSexOptions,
    collectMaternalStatus, sexEditingDisabled, maternalStatusEditingDisabled,
    identifierPanelName, identifierLabel, identifierError, identifierInvalid, identifierHasValue,
    assessmentUnavailable, proposedAssessmentPath, canStartAssessment, canSaveDemographics,
    trackLabel, assessmentLabel, demographicItems: baseDemographicItems, setAge, setAgeUnit, setSex, setMaternalStatus, setIdentifier,
    beginEdit: beginDemographicsEdit, acceptEdit: acceptDemographicsEdit,
    cancelEdit: restoreDemographicsEdit, answerValues: demographicAnswerValues,
  } = demographics
  const showUnavailableAssessment = ref(false)
  const unavailableAssessmentMessage = computed(() => translationFor(
    'app.assessment_unavailable.message',
    `${partnerConfigStore.name || 'This engagement'} is not set up to assess patients of this demographic.`,
    { engagement: partnerConfigStore.name || translationFor('app.assessment_unavailable.engagement', 'This engagement') },
  ));
  const availablePatientGroups = computed(() => Array.from(new Set(guidedModeStore.tracks
    .filter(item => item.path && assessmentDefinitions.value[item.path])
    .map(item => translationFor(
      `tracks.${item.name}.title`,
      translationFor(`condition_patient_type.${item.path}`, item.path || ''),
    )))))
  watch([proposedAssessmentPath, assessmentUnavailable, ageInvalid], () => {
    showUnavailableAssessment.value = assessmentUnavailable.value && !ageInvalid.value
  })
  const baseDefinition = computed(() => activeAssessmentPath.value ? assessmentDefinitions.value[activeAssessmentPath.value] || null : null)
  const dangerSignCodes = computed(() => (
    trackStore.dangerSignsEnabled && activeAssessmentPath.value
      ? getDangerSignQuestionOrder(activeAssessmentPath.value)
      : []
  ))
  const definition = computed(() => withDangerSignScreen(baseDefinition.value, dangerSignCodes.value))
  const symptomSelection = useGuidedSymptoms({ store: guidedModeStore, answers, activeAssessmentPath })
  const eligibilityExcludedCodes = computed(() => (activeAssessmentPath.value
    ? guidedModeStore.clinicalContent.questionEligibility?.[activeAssessmentPath.value] || []
    : []))
  const excludedDataPointCodes = computed(() => [
    ...eligibilityExcludedCodes.value,
    ...symptomSelection.recordedDataPointCodes.value,
  ])
  const focusedPlan = useGuidedFocusedPlan({
    store: guidedModeStore,
    answers,
    activeAssessmentPath,
    definition,
    excludedCodes: eligibilityExcludedCodes,
    escalated: computed(() => trackStore.initialAssessmentType === 'focused' && trackStore.comprehensive),
    reportError: error => proxy?.$sentry?.captureException(error),
  })
  const symptomSummary = computed(() => [
    ...symptomSelection.startedFocusedAssessments.value
      .map(symptom => translationFor(symptom.labelToken, symptom.id)),
    ...(guidedModeStore.otherSymptomSelected
      ? [translationFor('app.assessment_mode.guided.symptoms.other', 'Other symptom not listed')]
      : []),
  ])
  const focusedPanelNames = computed(() => (
    trackStore.initialAssessmentType === 'focused' && !trackStore.comprehensive
      ? guidedModeStore.focusedPanelNames
      : undefined
  ))
  const allSteps = computed(() => focusedPlan.steps.value ?? assessmentQuestionSteps(
    definition.value,
    excludedDataPointCodes.value,
    focusedPanelNames.value,
  ))
  const visibleSteps = computed(() => navigationSteps.value
    ?? allSteps.value.filter(step => isStepVisible(step, answers.value)))
  watch(currentIndex, index => { guidedModeStore.currentIndex = index }, { flush: 'sync' })
  const keptConceptIds = computed(() => {
    const introduced = new Set(guidedModeStore.introducedFocusedStepIds)
    const retained = assessmentQuestionSteps(definition.value).flatMap(function conceptsForStep(step): string[] {
      if (step.kind === 'group') return (step.steps || []).flatMap(conceptsForStep)
      if (!step.id || !introduced.has(step.id)) return []
      const question = questionForStep(definition.value, step)
      return question && answers.value[question.conceptId] !== undefined ? [question.conceptId] : []
    })
    return Array.from(new Set([...symptomSelection.keptConceptIds.value, ...retained]))
  })
  const review = useGuidedReviewFlow({ answers, currentIndex, definition, visibleSteps })
  const {
    editStepKey: reviewEditStepKey,
    showFollowUpWarning,
    currentStepIndex,
    reset: resetReviewEditState,
  } = review
  const autoProgress = computed(() => partnerConfigStore.autoProgress)
  const questionnaire = useGuidedQuestionnaire({
    answers,
    autoProgress,
    currentIndex,
    currentStepIndex,
    definition,
    visibleSteps,
    translate: translationFor,
    formatDate: date => proxy?.$d?.(date, 'short') ?? date.toLocaleDateString(),
    formatNumber: value => proxy?.$n?.(value, 'decimal') ?? String(value),
    preferredWidget: preferredWidgetFor,
    onAnswerChanged: () => {
      forgetForwardPath()
      return review.requiresFollowUpConfirmation()
    },
    onAutoAdvance: goNext,
    onAnswersRemoved: focusedPlan.discardInvalidConcepts,
    keptConceptIds,
    keptSymptomConceptIds: symptomSelection.keptConceptIds,
    settledConceptIds,
  })
  const demographicItems = computed(() => questionnaire.summaryBmi.value === null
    ? baseDemographicItems.value
    : [...baseDemographicItems.value, {
      id: 'body_mass_index',
      label: translationFor('questions.body_mass_index.title', 'Body Mass Index'),
      value: proxy?.$n?.(questionnaire.summaryBmi.value, 'decimal') ?? String(questionnaire.summaryBmi.value),
      readOnly: true,
    }])
  const {
    currentStep, currentGroup, currentAnswer, currentInputAnswer, currentValidation,
    questionValidationMessageToken,
    questionMedia, questionPlaybackRate, groupMedia, questionCollection, questionCollectionType,
    questionAllowsUnknown, questionAllowsMultiple, questionTitle, questionPrompt, questionInstructions,
    descPrompt, groupPrompt, groupOptions, selectedGroupOptions, questionOptions, selectedOptions,
    displayUnit, numericStep, questionWidget, inputId, isLastQuestion, questionCanContinue,
    answeredItems, bloodPressureRanges, bloodPressureSystolic, bloodPressureDiastolic,
    bloodPressureSystolicInvalid, bloodPressureDiastolicInvalid, bloodPressureSystolicWarning,
    bloodPressureDiastolicWarning, guidedDateValue, guidedDateMin, guidedDateMax, guidedDateInvalid,
    setAnswer, selectOption, selectGroupOption, setBloodPressurePart, setUnknownAnswer,
    setGuidedDate, syncSpecialInputState, setQuestionWidget, clearAutoAdvanceTimer,
    // Sequential group support
    isSequentialGroup, currentNestedIndex, sequentialGroupSteps, isLastInSequentialGroup, resetNestedIndex,
    sequentialGroupQuestionItems,
    // Sequential group actions
    setSequentialGroupAnswer, selectSequentialGroupOption,
    setSequentialGroupBloodPressurePart, setSequentialGroupUnknownAnswer, setSequentialGroupDate,
  } = questionnaire
  const currentSettlementKey = () => (view.value === 'question' && currentStep.value ? stepKey(currentStep.value) : '')
  function rememberCurrentPage() {
    if (view.value !== 'question' || !focusedPlan.active.value || !currentStep.value) return
    const oldKey = stepKey(currentStep.value)
    const pageId = focusedPlan.rememberPage(currentStep.value)
    guidedModeStore.focusedCurrentPageId = pageId
    if (reviewEditStepKey.value === oldKey) reviewEditStepKey.value = pageId
    const index = visibleSteps.value.findIndex(step => stepKey(step) === pageId)
    if (index >= 0 && !reviewEditStepKey.value) currentIndex.value = index
  }
  // Question ids rather than page keys, so pages seen on one path still count after a path change.
  function rememberSeenSteps(steps: GuidedModeStep[]) {
    const seen = new Set(guidedModeStore.seenStepIds)
    const added = Array.from(new Set(steps.flatMap(leafIdsForStep))).filter(id => !seen.has(id))
    if (added.length) guidedModeStore.seenStepIds = [...guidedModeStore.seenStepIds, ...added]
  }
  function rememberCurrentStepSeen() {
    if (view.value === 'question' && currentStep.value) rememberSeenSteps([currentStep.value])
  }
  // History pages were all shown, also in encounters saved before seenStepIds existed.
  function rememberHistorySeen(keys: string[]) {
    const history = new Set(keys)
    rememberSeenSteps(visibleSteps.value.filter(step => history.has(stepKey(step))))
  }
  function settleCurrentStep() {
    if (!navigating.value && !focusedPlan.evaluating.value && !isEvaluatingEscalation.value) {
      rememberCurrentPage()
      rememberCurrentStepSeen()
    }
    const key = currentSettlementKey()
    const step = currentStep.value
    if (key !== revealAnsweredStepKey) revealAnsweredStepKey = ''
    const confirmed = new Set(guidedModeStore.confirmedConceptIds)
    settledConceptIds.value = key && !revealAnsweredStepKey && step?.kind === 'group'
      && hasOpenVisibleQuestion(step, definition.value, answers.value)
      ? confirmedNestedConceptIds(step).filter(conceptId => confirmed.has(conceptId))
      : []
  }
  watch(currentSettlementKey, settleCurrentStep, { immediate: true })
  // Reset only when the displayed screen changes, after its content has rendered.
  watch([view, currentSettlementKey], scrollToTop, { flush: 'post' })
  function confirmedNestedConceptIds(step: GuidedModeStep): string[] {
    return (step.steps || []).flatMap(nestedStep => {
      const conceptId = questionForStep(definition.value, nestedStep)?.conceptId
      return conceptId && isStepVisible(nestedStep, answers.value)
        && isStepAnswered(nestedStep, definition.value, answers.value) ? [conceptId] : []
    })
  }
  const stepCanContinue = computed(() => !focusedPlan.evaluating.value && questionCanContinue.value)
  const heading = computed(() => view.value === 'demographics'
    ? editingDemographics.value ? 'Edit demographics' : translationFor('app.assessment_mode.guided.new_consultation', 'New consultation')
    : view.value === 'symptoms' ? translationFor('app.assessment_mode.guided.symptoms.title', 'Symptoms')
    : view.value === 'completion'
      ? translationFor('app.assessment_mode.guided.completion_prompt.title', 'Assessment')
    : view.value === 'question' ? (groupPrompt.value || questionTitle.value)
      : translationFor('app.summary.title', 'Assessment summary'))

  // Calculate total questions count including nested questions in sequential groups
  const totalQuestionsCount = computed(() => {
    return visibleSteps.value.reduce((count, step) => {
      if (step.kind === 'group' && !step.multiselect) {
        // Sequential group: count each nested question
        return count + (step.steps?.length || 0)
      }
      // Standalone question or multiselect group: count as 1
      return count + 1
    }, 0)
  })
  const isFocusedOpen = computed(() => trackStore.initialAssessmentType === 'focused' && !trackStore.comprehensive)
  const isFocusedOrigin = computed(() => trackStore.initialAssessmentType === 'focused')
  const isEscalatedFromFocused = computed(() => isFocusedOrigin.value && trackStore.comprehensive)
  const openVisibleStepCount = computed(() => visibleSteps.value
    .filter(step => hasOpenVisibleQuestion(step, definition.value, answers.value)).length)
  const answeredVisibleStepCount = computed(() => visibleSteps.value.length - openVisibleStepCount.value)
  // Progress shows current step position (sequential groups count as 1 step, Symptoms is the first)
  const rawProgressPercent = computed(() => {
    if (view.value === 'demographics') return 0
    if (view.value === 'summary') return 100
    const stepCount = visibleSteps.value.length + 1
    if (isFocusedOrigin.value) {
      const base = guidedModeStore.progressBase ?? (visibleSteps.value.length ? 100 / stepCount : 0)
      if (view.value === 'symptoms') return base
      const leaving = view.value === 'question' && currentStep.value
        && !hasOpenVisibleQuestion(currentStep.value, definition.value, answers.value) ? 1 : 0
      const done = Math.max(0, answeredVisibleStepCount.value - guidedModeStore.progressBaseAnswered - leaving)
      const total = done + openVisibleStepCount.value + leaving
      if (total === 0) return view.value === 'completion' ? 100 : base
      return base + (100 - base) * (done / total)
    }
    if (view.value === 'symptoms') return 100 / stepCount
    return ((currentStepIndex.value + 2) / stepCount) * 100
  })
  const tracksProgressFloor = computed(() => isFocusedOrigin.value)
  const progressPercent = computed(() => tracksProgressFloor.value
    ? Math.max(rawProgressPercent.value, guidedModeStore.progressFloor)
    : rawProgressPercent.value)
  const progressSegments = computed(() => (isEscalatedFromFocused.value
    ? [100, view.value === 'summary' ? 100 : progressPercent.value]
    : [progressPercent.value]))
  watch([rawProgressPercent, tracksProgressFloor], ([value]) => {
    if (tracksProgressFloor.value && value > guidedModeStore.progressFloor) guidedModeStore.progressFloor = value
  }, { immediate: true })
  watch(openVisibleStepCount, (next, previous) => {
    if (isFocusedOrigin.value && next > previous) {
      guidedModeStore.rebaseProgress(guidedModeStore.progressFloor, answeredVisibleStepCount.value)
    }
  })
  const canContinue = computed(() => !navigating.value && !isEvaluatingEscalation.value && view.value !== 'completion' && (view.value === 'demographics'
    ? editingDemographics.value ? canSaveDemographics.value : canStartAssessment.value
    : view.value === 'symptoms' ? symptomSelection.canContinue.value && !focusedPlan.evaluating.value
    : view.value !== 'question' || stepCanContinue.value))

  function stepAnalyticsName(step: GuidedModeStep | null | undefined): string {
    if (!step) return GUIDED_SUMMARY_PANEL
    return questionForStep(definition.value, step)?.conceptId || stepKey(step)
  }
  const analyticsPanelName = computed(() => {
    if (view.value === 'demographics') return GUIDED_DEMOGRAPHICS_PANEL
    if (view.value === 'symptoms') return GUIDED_SYMPTOMS_PANEL
    if (view.value === 'summary') return GUIDED_SUMMARY_PANEL
    return stepAnalyticsName(currentStep.value)
  })
  const isBlockingSeverity = (severity: string | null | undefined) => severity === 'blocking' || severity === 'unconfigured'
  const mediaItems = computed<GuidedMediaItem[]>(() => {
    // Read before isSequentialGroup, or this computed goes stale on group answers.
    const groupItems = sequentialGroupQuestionItems.value
    if (isSequentialGroup.value) {
      return groupItems.map(item => ({
        conceptId: item.conceptId,
        media: item.media,
        answer: item.answer,
        collectionType: item.collectionType,
        isBlocking: isBlockingSeverity(item.validationSeverity),
      }))
    }
    const conceptId = questionnaire.currentConceptId.value
    if (view.value !== 'question' || !conceptId) return []
    return [{
      conceptId,
      media: questionMedia.value,
      answer: currentAnswer.value,
      collectionType: questionCollectionType.value,
      isBlocking: isBlockingSeverity(currentValidation.value?.severity),
    }]
  })
  const mediaPresentation = useGuidedMediaPresentation({
    items: mediaItems,
    isMultiQuestionPage: isSequentialGroup,
    screenKey: analyticsPanelName,
  })
  function toggleMediaSize(conceptId: string): void {
    mediaPresentation.toggleSize(conceptId)
  }
  function markMediaEntryComplete(conceptId: string): void {
    mediaPresentation.markEntryComplete(conceptId)
  }
  function toggleGroupMediaSize(): void {
    mediaPresentation.toggleGroupSize()
  }
  function toggleMediaAudio(conceptId: string): void {
    if (mediaPresentation.mediaState.value[conceptId]?.audioEnabled) mediaPresentation.stopAudible()
    else mediaPresentation.requestAudible(conceptId)
  }
  const questionAudioEnabled = computed(() =>
    mediaPresentation.mediaState.value[questionnaire.currentConceptId.value]?.audioEnabled ?? false)
  function toggleQuestionAudio(): void {
    toggleMediaAudio(questionnaire.currentConceptId.value)
  }
  function trackCurrentStep(revisit = false) {
    if (view.value === 'completion') return
    if (view.value === 'summary') {
      trackGuidedSummaryViewed(visibleSteps.value.length, { revisit })
      return
    }
    trackGuidedQuestionViewed({
      panelName: analyticsPanelName.value,
      stepIndex: view.value === 'question' ? currentStepIndex.value : undefined,
      stepCount: visibleSteps.value.length,
      assessmentPath: activeAssessmentPath.value,
    }, { revisit })
  }
  watch(analyticsPanelName, () => trackCurrentStep())
  onMounted(() => {
    const started = trackGuidedModeStarted({
      encounterId: encounterStore.uuid,
      routeName: proxy?.$route?.name ? String(proxy.$route.name) : 'AssessmentModeGuided',
    })
    trackCurrentStep(!started)
  })

  function translationFor(token: string, fallback: string, values?: Record<string, string | number>) {
    const value = proxy?.$t ? proxy.$t(token, values) : token
    return value === token ? fallback : value
  }
  function preferredWidgetFor(conceptId: string): string {
    const getter = PREFERRED_WIDGET_GETTERS[conceptId]
    if (!getter) return ''
    return preferenceStore[getter] || ''
  }
  function startAssessment() {
    const demographicAnswers = demographicAnswerValues()
    if (!canStartAssessment.value || proposedAssessmentPath.value === null || !demographicAnswers) return
    const isRequestedHandoff = requestedAssessmentPath.value !== null
    clearAutoAdvanceTimer()
    resetReviewEditState()
    acceptDemographicsEdit()
    guidedModeStore.clearObservations()
    questionnaire.resetQuestionnaire()
    activeAssessmentPath.value = proposedAssessmentPath.value
    guidedModeStore.clearRequestedAssessmentPath()
    currentIndex.value = 0
    answers.value = demographicAnswers
    symptomSelection.restartSelections()
    guidedModeStore.setFocusedAssessmentMetadata([], [])
    guidedModeStore.clearProgress()
    guidedModeStore.confirmedConceptIds = []
    guidedModeStore.visitedStepKeys = []
    guidedModeStore.forwardStepKeys = []
    guidedModeStore.seenStepIds = []
    guidedModeStore.answerEscalation = null
    guidedModeStore.keptEscalationStepIds = []
    if (!isRequestedHandoff) trackStore.resetAssessmentState()
    focusedPlan.reset()
    view.value = 'symptoms'

    syncTextModePatientAndTrack()
    trackGuidedAssessmentSelected({
      assessmentPath: activeAssessmentPath.value ?? '',
      stepCount: visibleSteps.value.length,
    })
  }
  function syncTextModePatientAndTrack() {
    const patient = buildGuidedPatient({
      age: age.value,
      ageUnit: ageUnit.value,
      sex: sex.value,
      maternalStatus: maternalStatus.value,
      assessmentPath: activeAssessmentPath.value,
      patientId: identifierValue.value,
    })
    patientStore.setPatient(patient)
    void setTrackByPatientType({ tracks: useTrackStore(proxy?.$pinia).all, patientType: patient.type })
  }
  function editDemographics() {
    navigationToken += 1
    trackGuidedAnswerEdited(GUIDED_DEMOGRAPHICS_PANEL)
    clearAutoAdvanceTimer()
    resetReviewEditState()
    beginDemographicsEdit()
    view.value = 'demographics'
  }
  async function saveDemographics() {
    const demographicAnswers = demographicAnswerValues()
    if (!canSaveDemographics.value || !demographicAnswers) return
    const previousAnswers = answers.value
    const previousSelectedSymptomIds = [...guidedModeStore.selectedSymptomIds]
    const previousRecordedSymptomIds = [...guidedModeStore.recordedSymptomIds]
    answers.value = {
      ...answers.value,
      ...demographicAnswers,
    }
    symptomSelection.recordSelections(previousAnswers)
    if (trackStore.initialAssessmentType === 'focused' && !trackStore.comprehensive) {
      const evaluated = await evaluateFocusedScope()
      if (!evaluated) {
        answers.value = previousAnswers
        guidedModeStore.selectedSymptomIds = previousSelectedSymptomIds
        guidedModeStore.recordedSymptomIds = previousRecordedSymptomIds
        return
      }
    }
    acceptDemographicsEdit()
    syncTextModePatientAndTrack()
    resumeReview()
  }
  function cancelDemographicsEdit() {
    restoreDemographicsEdit()
    view.value = 'summary'
  }
  function resumeReview() {
    const firstUnansweredIndex = findNextUnansweredStepIndex(
      visibleSteps.value,
      definition.value,
      answers.value,
    )
    if (firstUnansweredIndex >= 0) {
      currentIndex.value = firstUnansweredIndex
      reviewEditStepKey.value = stepKey(visibleSteps.value[firstUnansweredIndex])
      view.value = 'question'
    } else openSummaryOrCompletion()
  }
  function editSymptoms() {
    navigationToken += 1
    trackGuidedAnswerEdited(GUIDED_SYMPTOMS_PANEL)
    clearAutoAdvanceTimer()
    resetReviewEditState()
    symptomSelection.beginEdit()
    view.value = 'symptoms'
    scrollToTop()
  }
  function cancelSymptomsEdit() {
    if (focusedPlan.evaluating.value) focusedPlan.cancelEvaluation()
    symptomSelection.cancelEdit()
    symptomSelection.recordSelections()
    view.value = 'summary'
    scrollToTop()
  }
  function explicitFocusedOptions(): string[] {
    return Array.from(new Set([
      ...symptomSelection.startedFocusedAssessments.value.flatMap(symptom => symptom.focusedOptions),
      ...focusedPlan.startedFocusedOptions.value,
    ]))
  }
  function focusedScopeEvaluationKey(): string {
    return JSON.stringify({
      activeAssessmentPath: activeAssessmentPath.value,
      answers: answers.value,
      classifications: trackStore.classifications,
      explicitFocusedOptions: explicitFocusedOptions(),
      originFindingsByOption: focusedPlan.originFindingsByOption.value,
      initialAssessmentType: trackStore.initialAssessmentType,
      comprehensive: trackStore.comprehensive,
    })
  }
  function symptomSelectionEvaluationKey(): string {
    return JSON.stringify({
      selectedSymptomIds: guidedModeStore.selectedSymptomIds,
      otherSymptomSelected: guidedModeStore.otherSymptomSelected,
      comprehensiveSelected: guidedModeStore.comprehensiveSelected,
    })
  }
  function demographicsEvaluationKey(): string {
    return JSON.stringify({
      age: age.value,
      ageUnit: ageUnit.value,
      sex: sex.value,
      maternalStatus: maternalStatus.value,
      identifierValue: identifierValue.value,
    })
  }
  function canEvaluateFocusedScope(startFocusedOnSuccess: boolean): boolean {
    if (startFocusedOnSuccess) {
      return trackStore.initialAssessmentType === null
        || trackStore.initialAssessmentType === undefined
    }
    if (trackStore.initialAssessmentType !== 'focused') return false
    if (!trackStore.comprehensive) return true
    return guidedModeStore.consultationPath !== 'comprehensive' && escalationAnswersChanged()
  }
  function scopeInputsKey(): string {
    return JSON.stringify([answers.value, symptomSelectionEvaluationKey(), focusedPlan.originFindingsByOption.value])
  }
  // New answers only add MRX selections, so only a changed or removed answer can end an escalation.
  function escalationAnswersChanged(): boolean {
    return Object.entries(guidedModeStore.answerEscalation?.answers || {})
      .some(([conceptId, answer]) => (
        JSON.stringify(answers.value[conceptId]) !== JSON.stringify(answer)
      ))
  }
  function snapshotAnswers(): Record<string, GuidedAnswer> {
    return JSON.parse(JSON.stringify(Object.fromEntries(Object.entries(answers.value)
      .filter(([, answer]) => hasAnswer(answer)))))
  }
  function recordScopeResult(result: GuidedEscalationResult) {
    guidedModeStore.setFocusedAssessmentMetadata(result.panels, result.focusedSelections)
    if (!result.comprehensive) trackStore.setFilterConditions(result.conditions)
    trackStore.streamlinedOptions = result.streamlinedOptions.map((option, index) => ({
      id: option.name,
      name: `streamlined.${option.name}`,
      value: option.name,
      ordering: index * 100,
      selected: option.selected,
      preselect: option.preselect,
    }))
    trackStore.thresholdSet = result.thresholdSet
  }
  // Runs once the focused plan has caught up, so the pages, the scope and the bar change together.
  // Answers and symptoms stay editable meanwhile; a result for older ones waits for the next Next.
  function settleEvaluatedScope(planReady: boolean) {
    const evaluated = evaluatedScope
    evaluatedScope = null
    if (!evaluated || scopeInputsKey() !== evaluated.inputs) return
    if (trackStore.initialAssessmentType !== 'focused') return
    const { result } = evaluated
    const escalation = guidedModeStore.answerEscalation
    if (result.comprehensive) {
      if (escalation) {
        guidedModeStore.answerEscalation = { ...escalation, answers: snapshotAnswers() }
      }
      return
    }
    if (!trackStore.comprehensive || !planReady) return
    if (guidedModeStore.consultationPath === 'comprehensive') return
    // Answers already given stay in the Summary and the payload; only unanswered questions drop out.
    guidedModeStore.keptEscalationStepIds = Array.from(new Set([
      ...guidedModeStore.keptEscalationStepIds,
      ...visibleSteps.value.flatMap(answeredLeafIds),
    ]))
    trackStore.returnToFocusedAssessment()
    recordScopeResult(result)
    guidedModeStore.answerEscalation = null
    guidedModeStore.restartProgress(escalation?.progressFloor ?? 0, answeredVisibleStepCount.value)
  }
  async function evaluateFocusedScope(startFocusedOnSuccess = false): Promise<boolean> {
    if (!definition.value || !canEvaluateFocusedScope(startFocusedOnSuccess)) {
      return true
    }
    if (isEvaluatingEscalation.value) return false

    isEvaluatingEscalation.value = true
    const token = navigationToken
    try {
      while (definition.value && canEvaluateFocusedScope(startFocusedOnSuccess)) {
        const evaluationDefinition = definition.value
        const evaluationKey = focusedScopeEvaluationKey()
        const symptomSelectionKey = symptomSelectionEvaluationKey()
        const demographicsKey = demographicsEvaluationKey()
        const result = await evaluateGuidedEscalation({
          definition: evaluationDefinition,
          answers: answers.value,
          classifications: trackStore.classifications as GuidedModeClassification[],
          explicitFocusedOptions: explicitFocusedOptions(),
          originFindingsByOption: focusedPlan.originFindingsByOption.value,
        })

        // Answer and symptom controls remain interactive while MRX is running.
        // A result for older inputs must not change the canonical assessment scope.
        if (token !== navigationToken || symptomSelectionEvaluationKey() !== symptomSelectionKey
          || demographicsEvaluationKey() !== demographicsKey) return false
        if (definition.value !== evaluationDefinition || focusedScopeEvaluationKey() !== evaluationKey) continue

        // Validate all MRX panel identifiers before changing the canonical scope.
        // An invalid response must keep the current question and focused scope.
        assessmentQuestionSteps(definition.value, excludedDataPointCodes.value, result.panels)
        if (startFocusedOnSuccess) trackStore.startFocusedAssessment()
        evaluatedScope = { result, inputs: scopeInputsKey() }
        if (!result.comprehensive && trackStore.comprehensive) return true
        recordScopeResult(result)
        if (result.comprehensive) {
          const escalating = !trackStore.comprehensive
          const progressFloor = guidedModeStore.answerEscalation?.progressFloor
            ?? guidedModeStore.progressFloor
          trackStore.escalateToComprehensiveAssessment({ keepFocusedSelections: true })
          guidedModeStore.clearFocusedPanels()
          if (escalating) restartProgressForComprehensive()
          guidedModeStore.answerEscalation = { answers: snapshotAnswers(), progressFloor }
        }
        return true
      }
      return true
    } catch (error) {
      // An escalation that cannot be re-checked stays in place, so offline work is never blocked.
      const keepsEscalation = trackStore.comprehensive && Boolean(guidedModeStore.answerEscalation)
      if (!keepsEscalation) {
        networkError.value = isNetworkError(error)
          || (navigator.onLine === false && !partnerConfigStore.offline)
      }
      logger.error(
        LogCategory.ENCOUNTER,
        'guided.escalation.evaluation.failed',
        {
          assessmentPath: activeAssessmentPath.value,
          type: error instanceof Error ? error.name : typeof error,
          reason: error instanceof Error ? error.message : String(error),
          view: view.value,
        },
        'Guided assessment scope evaluation failed',
      ).then()
      return keepsEscalation
    } finally {
      isEvaluatingEscalation.value = false
    }
  }
  function restartProgressForComprehensive() {
    guidedModeStore.restartProgress(0, answeredVisibleStepCount.value)
  }
  function forgetForwardPath() {
    if (!guidedModeStore.forwardStepKeys.length) return
    rememberHistorySeen(guidedModeStore.forwardStepKeys)
    guidedModeStore.forwardStepKeys = []
  }
  function pushStepKey(stack: 'visitedStepKeys' | 'forwardStepKeys', key: string) {
    const keys = guidedModeStore[stack]
    if (key && keys[keys.length - 1] !== key) guidedModeStore[stack] = [...keys, key]
  }
  function popStepIndex(stack: 'visitedStepKeys' | 'forwardStepKeys', currentKey: string): number {
    const keys = [...guidedModeStore[stack]]
    let index = -1
    while (keys.length && index < 0) {
      const key = keys.pop()!
      if (key !== currentKey) index = visibleSteps.value.findIndex(step => stepKey(step) === key)
    }
    guidedModeStore[stack] = keys
    return index
  }
  // Next skips pages that are already answered; Back still walks back through them in order.
  function retraceSkippedPages(fromKey: string) {
    if (!fromKey || reviewEditStepKey.value) return
    const fromIndex = visibleSteps.value.findIndex(step => stepKey(step) === fromKey)
    const toIndex = view.value === 'question' ? currentStepIndex.value : visibleSteps.value.length
    if (fromIndex < 0 || toIndex <= fromIndex + 1) return
    const seen = new Set(guidedModeStore.seenStepIds)
    const visited = new Set(guidedModeStore.visitedStepKeys)
    const skipped = visibleSteps.value.slice(fromIndex + 1, toIndex)
      .filter(step => leafIdsForStep(step).some(id => seen.has(id))).map(stepKey)
      .filter(key => !visited.has(key))
    if (!skipped.length) return
    guidedModeStore.visitedStepKeys = [...guidedModeStore.visitedStepKeys, ...skipped]
    if (view.value !== 'completion') return
    // Back from the prompt goes to its return page; the saved index keeps it through a pause.
    completionReturnKey = skipped[skipped.length - 1]
    currentIndex.value = visibleSteps.value.findIndex(step => stepKey(step) === completionReturnKey)
  }
  // A Summary edit that opens new pages becomes a normal pass; Back starts at the edited page.
  function rejoinPathAfterEdit(editKey: string) {
    if (!editKey || view.value !== 'question' || reviewEditStepKey.value) return
    const indexOf = (key: string) => visibleSteps.value.findIndex(step => stepKey(step) === key)
    const editIndex = indexOf(editKey)
    if (editIndex < 0 || currentStepIndex.value <= editIndex) return
    rememberHistorySeen([...guidedModeStore.visitedStepKeys, ...guidedModeStore.forwardStepKeys])
    const before = guidedModeStore.visitedStepKeys
      .filter(key => indexOf(key) >= 0 && indexOf(key) < editIndex)
    guidedModeStore.visitedStepKeys = [...before, editKey]
    retraceSkippedPages(editKey)
  }
  function nextOutstandingStepIndex(afterIndex: number): number {
    const isOpen = (step: GuidedModeStep) => hasOpenVisibleQuestion(step, definition.value, answers.value)
    const nextIndex = visibleSteps.value.findIndex((step, index) => index > afterIndex && isOpen(step))
    if (nextIndex >= 0) return nextIndex
    return visibleSteps.value.findIndex(isOpen)
  }
  function leafIdsForStep(step: GuidedModeStep): string[] {
    if (step.kind === 'group') return (step.steps || []).flatMap(leafIdsForStep)
    return step.id ? [step.id] : []
  }
  function answeredLeafIds(step: GuidedModeStep): string[] {
    if (step.kind === 'group') return (step.steps || []).flatMap(answeredLeafIds)
    return step.id && isStepAnswered(step, definition.value, answers.value) ? [step.id] : []
  }
  function pendingFocusedStepIndex(): number {
    const leaves = visibleSteps.value.flatMap(step => step.kind === 'group' ? step.steps || [] : [step])
    const mustConfirm = new Set(focusedPlan.askStepIds.value)
    guidedModeStore.pendingFocusedStepIds = guidedModeStore.pendingFocusedStepIds.filter(
      id => leaves.some(step => step.id === id && isStepVisible(step, answers.value)
        && (mustConfirm.has(id) || !isStepAnswered(step, definition.value, answers.value))),
    )
    for (const stepId of guidedModeStore.pendingFocusedStepIds) {
      const index = visibleSteps.value.findIndex(step => leafIdsForStep(step).includes(stepId))
      if (index >= 0) return index
    }
    return -1
  }
  function routeFocusedQueue(): boolean {
    const pendingIndex = pendingFocusedStepIndex()
    if (pendingIndex >= 0) {
      if (reviewEditStepKey.value) resetReviewEditState()
      currentIndex.value = pendingIndex
      view.value = 'question'
      return true
    }
    const resumeKey = guidedModeStore.focusedResumeStepKey
    if (!resumeKey) return false
    guidedModeStore.focusedResumeStepKey = null
    const resumeIndex = visibleSteps.value.findIndex(step => stepKey(step) === resumeKey)
    const nextIndex = nextOutstandingStepIndex(resumeIndex)
    if (nextIndex >= 0) {
      currentIndex.value = nextIndex
      view.value = 'question'
    } else {
      openSummaryOrCompletion()
    }
    return true
  }
  async function goNext() {
    if (!stepCanContinue.value || isEvaluatingEscalation.value || navigating.value) return
    questionnaire.clearStaleAnswers()
    focusedPlan.discardInvalidConcepts(questionnaire.hiddenConceptIds.value)
    rememberCurrentPage()
    rememberCurrentStepSeen()
    const fromKey = view.value === 'question' && currentStep.value ? stepKey(currentStep.value) : ''
    const editing = Boolean(reviewEditStepKey.value)
    navigationSteps.value = visibleSteps.value
    navigating.value = true
    const token = ++navigationToken
    try {
      await goNextWithPlan(token)
    } finally {
      if (token === navigationToken) {
        navigationSteps.value = null
        navigating.value = false
        settleCurrentStep()
        if (editing) rejoinPathAfterEdit(fromKey)
        else retraceSkippedPages(fromKey)
      }
    }
  }
  async function goNextWithPlan(token: number) {
    clearAutoAdvanceTimer()
    if (view.value === 'question' && currentStep.value?.kind === 'group') {
      guidedModeStore.confirmedConceptIds = Array.from(new Set([
        ...guidedModeStore.confirmedConceptIds,
        ...confirmedNestedConceptIds(currentStep.value),
      ]))
    }
    if (view.value === 'question' && currentStep.value && !reviewEditStepKey.value) {
      pushStepKey('visitedStepKeys', stepKey(currentStep.value))
    }
    if (view.value === 'question') {
      trackGuidedQuestionAnswered({
        unknownSelected: String(currentAnswer.value ?? '').toLowerCase() === 'unknown',
      })
    }

    const previousStepKey = currentStep.value ? stepKey(currentStep.value) : null
    const previousVisibleStepKeys = visibleSteps.value.map(step => stepKey(step))
    if (focusedPlan.active.value) {
      const confirmed = currentStep.value ? leafIdsForStep(currentStep.value).flatMap(id => {
        const step = flattenSteps(definition.value).find(item => item.id === id)
        const question = step ? questionForStep(definition.value, step) : undefined
        return question ? [question.conceptId] : []
      }) : []
      const maxPasses = guidedModeStore.clinicalContent.symptoms?.[activeAssessmentPath.value!]?.length || 1
      for (let pass = 0; pass <= maxPasses; pass += 1) {
        const before = JSON.stringify([answers.value, guidedModeStore.assumedSymptomAnswers])
        const beforeAnswers = answers.value
        focusedPlan.commitEntryPointRules(pass === 0 ? confirmed : [])
        questionnaire.clearStaleAnswers(new Set(Object.keys(beforeAnswers).filter(id => !(id in answers.value))))
        focusedPlan.discardInvalidConcepts(questionnaire.hiddenConceptIds.value)
        if (before === JSON.stringify([answers.value, guidedModeStore.assumedSymptomAnswers])) break
      }
    }
    // Without the setting Next must stay synchronous, as it is for every other engagement.
    if (trackStore.dangerSignsEnabled) {
      const dangerSignOutcome = await dangerSignCheck.runCheck(toConversionObservations(currentObservations()))
      if (dangerSignOutcome === 'failed' && token === navigationToken) networkError.value = true
      if (dangerSignOutcome !== 'continue' || token !== navigationToken) return
    }
    const wasComprehensive = trackStore.comprehensive
    const previousPanels = [...guidedModeStore.focusedPanelNames]
    evaluatedScope = null
    if (canEvaluateFocusedScope(false)) {
      const evaluated = await evaluateFocusedScope()
      if (!evaluated || token !== navigationToken) return
    }
    if (!focusedPlan.active.value) {
      settleEvaluatedScope(true)
      navigationSteps.value = null
    }
    else {
      const previousPending = new Set(guidedModeStore.pendingFocusedStepIds)
      const triggerStepId = currentStep.value ? leafIdsForStep(currentStep.value).slice(-1)[0] : undefined
      const planReady = await focusedPlan.evaluate({ commitRules: false, triggerStepId })
      if (token !== navigationToken) return
      settleEvaluatedScope(planReady)
      navigationSteps.value = null
      const addedPending = guidedModeStore.pendingFocusedStepIds.some(id => !previousPending.has(id))
      if (addedPending && !guidedModeStore.focusedResumeStepKey) {
        guidedModeStore.focusedResumeStepKey = previousStepKey
      }
      const index = visibleSteps.value.findIndex(step => stepKey(step) === previousStepKey)
      if (index >= 0 && !reviewEditStepKey.value) currentIndex.value = index
      // Both routes can stay on the page just answered, where the step key watcher does not fire.
      if ((!reviewEditStepKey.value || addedPending) && routeFocusedQueue()) {
        settleCurrentStep()
        return
      }
      const visibleStepKeysChanged = previousVisibleStepKeys.join('\u0000')
        !== visibleSteps.value.map(step => stepKey(step)).join('\u0000')
      if (visibleStepKeysChanged && !reviewEditStepKey.value) {
        const nextIndex = nextOutstandingStepIndex(index)
        if (nextIndex >= 0) {
          currentIndex.value = nextIndex
          view.value = 'question'
        } else {
          openSummaryOrCompletion()
        }
        settleCurrentStep()
        return
      }
    }
    const assessmentScopeChanged = wasComprehensive !== trackStore.comprehensive
      || (!focusedPlan.active.value
        && previousPanels.join('\u0000') !== guidedModeStore.focusedPanelNames.join('\u0000'))
    if (assessmentScopeChanged) {
      resetReviewEditState()
      forgetForwardPath()
      const previousStepIndex = previousStepKey
        ? visibleSteps.value.findIndex(step => stepKey(step) === previousStepKey)
        : -1
      if (previousStepIndex >= 0) currentIndex.value = previousStepIndex
      const firstUnansweredIndex = nextOutstandingStepIndex(previousStepIndex)
      if (firstUnansweredIndex < 0) openSummaryOrCompletion()
      else {
        currentIndex.value = firstUnansweredIndex
        view.value = 'question'
      }
      settleCurrentStep()
      return
    }
    advance()
    settleCurrentStep()
  }
  function advance() {
    if (reviewEditStepKey.value) {
      const nextUnansweredIndex = review.nextUnansweredIndex()
      if (nextUnansweredIndex < 0) {
        resetReviewEditState()
        openSummaryOrCompletion()
      } else {
        review.moveToStep(nextUnansweredIndex)
      }
      return
    }

    const forwardIndex = popStepIndex('forwardStepKeys', currentStep.value ? stepKey(currentStep.value) : '')
    if (forwardIndex >= 0) {
      currentIndex.value = forwardIndex
      return
    }

    if (isFocusedOrigin.value) {
      const nextUnansweredIndex = nextOutstandingStepIndex(currentStepIndex.value)
      if (nextUnansweredIndex < 0) openSummaryOrCompletion()
      else currentIndex.value = nextUnansweredIndex
      return
    }

    // Sequential groups display all questions on one screen. Once they are all
    // answered we treat the group as a single step from a navigation standpoint:
    // move to the summary if there are no further steps, otherwise advance the
    // step index. The per-question nested index is purely visual; the user never
    // navigates between nested questions individually.
    //
    // Invariant: the predicate used here (driven by
    // `questionCanContinue` → `allSequentialGroupAnswered` in the questionnaire)
    // must agree with `isStepAnswered` / `findNextUnansweredStepIndex` in
    // `utils/step-engine.ts`, which is what the review-edit flow uses to pick
    // the next step when the user re-opens a summary item. Keeping that predicate
    // collection-aware (any non-empty answer counts for non-multiselect groups,
    // yes/no still gates multiselect groups) prevents editing an earlier item
    // from forcing the user through every already-completed numeric group.
    if (isSequentialGroup.value) {
      if (currentStepIndex.value >= visibleSteps.value.length - 1) finishQuestions()
      else currentIndex.value++
      return
    }

    if (isLastQuestion.value) finishQuestions()
    else currentIndex.value++
  }
  function finishQuestions() {
    if (routeFocusedQueue()) return
    const openIndex = focusedPlan.active.value
      ? findFirstOpenStepIndex(visibleSteps.value, definition.value, answers.value)
      : -1
    if (openIndex >= 0) currentIndex.value = openIndex
    else openSummaryOrCompletion()
  }
  function shouldOfferCompletion(): boolean {
    return isFocusedOpen.value
      && !guidedModeStore.summaryReached
      && !focusedPlan.evaluating.value
      && !guidedModeStore.pendingFocusedStepIds.length
      && findFirstOpenStepIndex(visibleSteps.value, definition.value, answers.value) < 0
  }
  function openSummaryOrCompletion() {
    completionReturnKey = view.value === 'question' && currentStep.value ? stepKey(currentStep.value) : ''
    if (shouldOfferCompletion()) {
      view.value = 'completion'
      scrollToTop()
    } else view.value = 'summary'
  }
  function completeFocusedAssessment() {
    if (view.value !== 'completion') return
    view.value = 'summary'
    scrollToTop()
  }
  function continueAssessment() {
    if (view.value !== 'completion') return
    if (!isFocusedOpen.value) {
      view.value = 'summary'
      return
    }
    navigationToken += 1
    clearAutoAdvanceTimer()
    forgetForwardPath()
    trackStore.escalateToComprehensiveAssessment({ keepFocusedSelections: true })
    guidedModeStore.answerEscalation = null
    guidedModeStore.clearFocusedPanels()
    restartProgressForComprehensive()
    resetReviewEditState()
    const firstUnansweredIndex = nextOutstandingStepIndex(-1)
    if (firstUnansweredIndex < 0) view.value = 'summary'
    else {
      currentIndex.value = firstUnansweredIndex
      view.value = 'question'
    }
    scrollToTop()
  }
  function scrollToTop() {
    void nextTick(() => window.scrollTo(0, 0))
  }
  async function continueFromSymptoms() {
    if (!symptomSelection.canContinue.value || isEvaluatingEscalation.value || focusedPlan.evaluating.value) return
    const editing = symptomSelection.editing.value
    const previousAnswers = answers.value
    const previousSelectedSymptomIds = [...guidedModeStore.selectedSymptomIds]
    const previousRecordedSymptomIds = [...guidedModeStore.recordedSymptomIds]
    symptomSelection.recordSelections()
    const recordedSelectedSymptomIds = [...guidedModeStore.selectedSymptomIds]

    evaluatedScope = null
    const workerChoseComprehensive = guidedModeStore.consultationPath === 'comprehensive'
    const keepsScope = trackStore.isComprehensiveScope && !canEvaluateFocusedScope(false)
    if (workerChoseComprehensive || keepsScope) {
      if (workerChoseComprehensive) guidedModeStore.answerEscalation = null
      if (trackStore.initialAssessmentType === 'focused') {
        if (!trackStore.comprehensive) {
          trackStore.escalateToComprehensiveAssessment({ keepFocusedSelections: true })
          restartProgressForComprehensive()
        }
      } else trackStore.startComprehensiveAssessment()
      guidedModeStore.clearFocusedPanels()
    } else {
      const startsFocusedScope = trackStore.initialAssessmentType === null
        || trackStore.initialAssessmentType === undefined
      const evaluated = await evaluateFocusedScope(startsFocusedScope)
      if (!evaluated) {
        const selectionChangedDuringEvaluation = guidedModeStore.selectedSymptomIds.join('\u0000')
          !== recordedSelectedSymptomIds.join('\u0000')
        answers.value = previousAnswers
        guidedModeStore.recordedSymptomIds = previousRecordedSymptomIds
        if (!selectionChangedDuringEvaluation) {
          guidedModeStore.selectedSymptomIds = previousSelectedSymptomIds
        }
        return
      }
    }

    resetReviewEditState()
    let planReady = true
    if (focusedPlan.active.value) {
      const token = ++navigationToken
      planReady = await focusedPlan.evaluate()
      if (token !== navigationToken) return
    }
    settleEvaluatedScope(planReady)
    symptomSelection.acceptEdit()
    enterQuestions(editing)
  }
  function enterQuestions(editing: boolean) {
    if (editing) resumeReview()
    else {
      currentIndex.value = 0
      if (visibleSteps.value.length) view.value = 'question'
      else openSummaryOrCompletion()
    }
    scrollToTop()
  }
  function retryAfterNetworkError() {
    networkError.value = false
    return handleNext()
  }
  function dismissNetworkError() {
    networkError.value = false
  }
  function handleNext() {
    if (view.value === 'demographics') return editingDemographics.value ? saveDemographics() : startAssessment()
    if (view.value === 'symptoms') return continueFromSymptoms()
    if (view.value === 'question') return goNext()
    if (view.value === 'completion') return
    completeReview()
  }
  function goPrevious() {
    navigationToken += 1
    dangerSignCheck.cancel()
    focusedPlan.cancelEvaluation()
    navigationSteps.value = null
    navigating.value = false
    clearAutoAdvanceTimer()  // Cancel any pending auto-advance
    if (view.value === 'completion') {
      const returnIndex = visibleSteps.value.findIndex(step => stepKey(step) === completionReturnKey)
      if (visibleSteps.value.length) {
        currentIndex.value = returnIndex >= 0
          ? returnIndex
          : Math.max(0, Math.min(currentIndex.value, visibleSteps.value.length - 1))
        view.value = 'question'
      } else {
        view.value = 'symptoms'
        scrollToTop()
      }
    } else if (view.value === 'summary') {
      resetReviewEditState()
      const lastAskedIndex = popStepIndex('visitedStepKeys', '')
      if (visibleSteps.value.length) {
        view.value = 'question'
        currentIndex.value = lastAskedIndex >= 0 ? lastAskedIndex : visibleSteps.value.length - 1
      } else {
        view.value = 'symptoms'
        scrollToTop()
      }
    } else if (view.value === 'question') {
      // `currentStepIndex` is review-aware: when `reviewEditStepKey` is set it
      // resolves from the edited key rather than `currentIndex`, so simply
      // decrementing `currentIndex` leaves the edited question still rendered.
      // Route the move through `review.moveToStep` while in review so the
      // `editStepKey` follows us to the prior step (the previous behaviour).
      const currentKey = currentStep.value ? stepKey(currentStep.value) : ''
      const askedIndex = reviewEditStepKey.value ? -1 : popStepIndex('visitedStepKeys', currentKey)
      if (askedIndex >= 0) {
        pushStepKey('forwardStepKeys', currentKey)
        currentIndex.value = askedIndex
      } else if (currentStepIndex.value > 0) {
        const previousIndex = currentStepIndex.value - 1
        if (reviewEditStepKey.value) review.moveToStep(previousIndex)
        else currentIndex.value = previousIndex
      } else {
        resetReviewEditState()
        view.value = 'symptoms'
        scrollToTop()
      }
    } else if (view.value === 'symptoms') {
      if (symptomSelection.editing.value) return cancelSymptomsEdit()
      activeAssessmentPath.value = null
      view.value = 'demographics'
    } else if (view.value === 'demographics' && editingDemographics.value) cancelDemographicsEdit()
    else proxy?.$router?.back()
  }
  function editQuestion(stepIndex: number) {
    navigationToken += 1
    dangerSignCheck.cancel()
    focusedPlan.cancelEvaluation()
    navigationSteps.value = null
    navigating.value = false
    clearAutoAdvanceTimer()  // Cancel any pending auto-advance
    const editedStep = visibleSteps.value[stepIndex]
    revealAnsweredStepKey = editedStep ? stepKey(editedStep) : ''
    settledConceptIds.value = []
    if (review.begin(stepIndex)) {
      view.value = 'question'
      trackGuidedAnswerEdited(stepAnalyticsName(visibleSteps.value[stepIndex]))
    }
  }
  function cancelFollowUpChange() {
    clearAutoAdvanceTimer()
    review.cancelChange()
    syncSpecialInputState(currentStep.value)
    view.value = 'summary'
  }
  function continueFollowUpChange() {
    review.continueChange()
    goNext()
  }
  function completeReview() {
    if (!definition.value) return
    void handOffToTriage()
  }
  function handOffToTriage(stopped = false) {
    questionnaire.clearStaleAnswers()
    focusedPlan.discardInvalidConcepts(questionnaire.hiddenConceptIds.value)
    const includedConceptIds = stopped
      ? prepareGuidedWeightOnStop({ definition: definition.value, guidedModeStore })
      : []
    guidedModeStore.setObservations(currentObservations(includedConceptIds))
    trackGuidedAssessmentCompleted({ questionsAnswered: answeredItems.value.length })
    return finish()
  }
  async function stopAssessment() {
    if (!definition.value || isFinishing) return
    navigationToken += 1
    focusedPlan.cancelEvaluation()
    navigationSteps.value = null
    navigating.value = false
    clearAutoAdvanceTimer()
    networkError.value = false
    if (view.value === 'question' && currentStep.value && !reviewEditStepKey.value) {
      pushStepKey('visitedStepKeys', stepKey(currentStep.value))
    }
    resetReviewEditState()
    overallAssessmentStore.openRiskAssessment()
    await handOffToTriage(true)
    // Set after leaving, so Back from triage opens the summary as after a normal finish, with no flash of it here.
    view.value = 'summary'
  }
  onDangerSignStop(stopAssessment)
  function currentObservations(additionalConceptIds: string[] = []): GuidedModeObservation[] {
    if (!definition.value) return []
    const includedQuestionConceptIds = new Set([
      ...DEMOGRAPHIC_CONCEPT_IDS,
      ...symptomSelection.recordedConceptIds.value,
      ...focusedPlan.startedConceptIds.value,
      ...keptConceptIds.value,
      ...additionalConceptIds,
      ...visibleSteps.value.flatMap(step => {
        const questionSteps = step.kind === 'group' ? step.steps || [] : [step]
        return questionSteps.flatMap(questionStep => {
          const question = questionForStep(definition.value, questionStep)
          return question ? [question.conceptId] : []
        })
      }),
    ])
    const observations = buildGuidedModeObservations(
      definition.value,
      answers.value,
      includedQuestionConceptIds,
    )
    if (identifierPanelName.value && identifierHasValue.value) {
      observations.push({
        name: identifierPanelName.value,
        value: identifierValue.value.trim(),
        type: 'text',
        category: 'patient',
        source: 'response',
      })
    }
    return observations
  }
  async function finish() {
    if (isFinishing || !definition.value) return
    isFinishing = true

    try {
      const observations = buildReviewObservations({
        definition: definition.value,
        steps: flattenSteps(definition.value),
        observations: guidedModeStore.observations,
      })
      const patient = buildGuidedPatient({
        age: age.value,
        ageUnit: ageUnit.value,
        sex: sex.value,
        maternalStatus: maternalStatus.value,
        assessmentPath: activeAssessmentPath.value,
        patientId: identifierValue.value,
      })

      applyGuidedAssessment({
        observations,
        patient,
        pinia: proxy?.$pinia,
        comprehensive: trackStore.comprehensive,
        focusedAssessments: guidedModeStore.focusedSelections,
        conditions: trackStore.comprehensive ? [] : trackStore.filterConditions,
      })

      await setTrackByPatientType({ tracks: trackStore.all, patientType: patient.type })

      setActiveTextModeContext({
        encounterId: encounterStore.uuid,
        routeName: proxy?.$route?.name ? String(proxy.$route.name) : 'AssessmentModeGuided',
        mode: 'guided',
        assessmentPath: activeAssessmentPath.value ?? undefined,
        guidedSession: getActiveTextModeContext()?.guidedSession,
      })

      await safePush(proxy?.$router, SHARED_JOURNEY_ROUTE)
    } finally {
      isFinishing = false
    }
  }
  function goBack() {
    proxy?.$router?.back()
  }

  return {
    showUnavailableAssessment, unavailableAssessmentMessage, availablePatientGroups,
    hasDefinitions, definition, trackLabel, assessmentLabel, assessmentUnavailable, view, heading,
    age, ageUnit, ageMinimum, ageMaximum, ageInvalid, demographicAgePrompt, demographicAgeTitle,
    sex, demographicSexPrompt, demographicSexOptions, maternalStatus, collectMaternalStatus, sexEditingDisabled, maternalStatusEditingDisabled,
    identifierLabel, identifierValue, identifierError, identifierInvalid, ageError,
    bloodPressureRanges, bloodPressureSystolic, bloodPressureDiastolic,
    bloodPressureSystolicInvalid, bloodPressureDiastolicInvalid,
    bloodPressureSystolicWarning, bloodPressureDiastolicWarning,
    descPrompt, guidedDateValue, guidedDateMin, guidedDateMax, guidedDateInvalid,
    questionTitle, questionPrompt, questionInstructions, questionOptions, questionCollection, questionCollectionType,
    currentGroup, groupPrompt, groupOptions, selectedGroupOptions,
    questionAllowsUnknown, questionAllowsMultiple, questionMedia, questionPlaybackRate, groupMedia,
    currentAnswer, currentInputAnswer, currentValidation, questionValidationMessageToken,
    selectedOptions, displayUnit,
    numericStep, questionWidget, inputId, progressPercent, progressSegments, totalQuestionsCount, canContinue, answeredItems, demographicItems, showFollowUpWarning,
    // Sequential group support
    isSequentialGroup, currentNestedIndex, sequentialGroupSteps, isLastInSequentialGroup,
    sequentialGroupQuestionItems,
    setAge, setAgeUnit, setSex, setIdentifier, setMaternalStatus, setBloodPressurePart, setUnknownAnswer, setGuidedDate, setAnswer, selectOption, selectGroupOption, setQuestionWidget,
    // Sequential group handlers
    setSequentialGroupAnswer, selectSequentialGroupOption,
    setSequentialGroupBloodPressurePart, setSequentialGroupUnknownAnswer, setSequentialGroupDate,
    mediaState: mediaPresentation.mediaState, toggleMediaSize, markMediaEntryComplete, toggleMediaAudio,
    questionAudioEnabled, toggleQuestionAudio,
    groupMediaState: mediaPresentation.groupMediaState, toggleGroupMediaSize,
    handleNext, goPrevious, goBack, editQuestion, editDemographics, editSymptoms, cancelFollowUpChange, continueFollowUpChange, finish, autoProgress,
    networkError, retryAfterNetworkError, dismissNetworkError,
    completeFocusedAssessment, continueAssessment,
    symptoms: symptomSelection.symptoms,
    symptomSummary,
    selectedSymptomIds: symptomSelection.selectedSymptomIds,
    otherSymptomSelected: symptomSelection.otherSymptomSelected,
    comprehensiveSelected: symptomSelection.comprehensiveSelected,
    toggleSymptom: (id: string) => {
      if (!focusedPlan.evaluating.value) symptomSelection.toggleSymptom(id)
    },
    setOtherSymptom: symptomSelection.setOtherSymptom,
    setComprehensive: symptomSelection.setComprehensive,
  }
}
